import { useState } from 'react';
import { useI18n } from '../i18n';
import { useToast } from './toast';

/**
 * Hook « copier dans le presse-papiers » mutualisé — source unique du motif
 * clipboard + toast + retour visuel transitoire, partagé par tous les boutons de copie
 * (liens de partage, BBCode `[img]`, lien d'accès, console superadmin).
 *
 * `copy(text, successMsg)` écrit `text` dans le presse-papiers puis, en cas de succès,
 * lève un drapeau `copied` transitoire (~2 s) ET un toast VERT (`successMsg`) ; en cas
 * d'échec (presse-papiers indisponible : permissions/contexte), un toast ROUGE
 * (`copy.error`). Le drapeau `copied` sert à teinter le bouton en VERT le temps du retour
 * — jamais la couleur seule (l'appelant garde l'icône « check » + le libellé « Copié ! »).
 *
 * `copy` RENVOIE `true` ssi le presse-papiers a bien accepté le texte. Un appelant qui gère
 * son propre retour visuel par CIBLE (une liste de boutons partageant un seul hook, où le
 * drapeau `copied` global ne suffit pas à désigner LA ligne copiée) s'en sert pour ne teinter
 * en vert QUE sur un vrai succès — un « check » vert au-dessus du toast rouge d'un échec de
 * copie serait un mensonge. Les appelants qui n'en ont pas besoin ignorent la valeur.
 */
export function useCopy(): {
  copied: boolean;
  copy: (text: string, successMsg: string) => Promise<boolean>;
} {
  const { t } = useI18n();
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const copy = async (text: string, successMsg: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast.success(successMsg);
      return true;
    } catch {
      // Presse-papiers indisponible (permissions/contexte) : le champ reste sélectionnable.
      toast.error(t('copy.error'));
      return false;
    }
  };

  return { copied, copy };
}
